/**
 * Automation Opportunity Finder
 *
 * Identifies specific AI automation opportunities based on tech stack input.
 * Returns ranked list of candidates with estimated impact.
 */

import type { Dimension } from "./scoring";
import type { TechStackInput } from "./techStacks";

export interface AutomationOpportunity {
  id: string;
  title: string;
  description: string;
  dimension: Dimension;
  effort: "hours" | "days" | "weeks" | "months";
  effortDays: number;
  impactHoursPerMonth: number;
  confidence: "high" | "medium" | "low";
  prerequisites: string[];
  relatedTech: string[];
  whyItMatters: string;
}

/** Master catalog of possible automation opportunities */
const OPPORTUNITY_CATALOG: AutomationOpportunity[] = [
  {
    id: "code-review-ai",
    title: "AI-Powered Code Review",
    description:
      "Integrate AI into pull request reviews to catch bugs, suggest improvements, and enforce coding standards automatically.",
    dimension: "code-generation",
    effort: "days",
    effortDays: 3,
    impactHoursPerMonth: 40,
    confidence: "high",
    prerequisites: ["GitHub/GitLab repository", "CI/CD pipeline"],
    relatedTech: ["TypeScript", "JavaScript", "Python", "Java", "Go"],
    whyItMatters:
      "Code reviews are a bottleneck in most teams. AI can handle 60-80% of routine feedback instantly.",
  },
  {
    id: "test-generation",
    title: "AI Test Generation",
    description:
      "Generate unit and integration tests from existing code, improving coverage with minimal manual effort.",
    dimension: "testing",
    effort: "days",
    effortDays: 5,
    impactHoursPerMonth: 30,
    confidence: "high",
    prerequisites: ["Test framework configured", "CI pipeline"],
    relatedTech: ["TypeScript", "Python", "Java", "React", "Django"],
    whyItMatters:
      "Most teams want higher test coverage but lack time. AI can generate meaningful tests in seconds.",
  },
  {
    id: "doc-generation",
    title: "Automated Documentation",
    description:
      "Generate and maintain API docs, README files, and onboarding guides from code and comments.",
    dimension: "documentation",
    effort: "days",
    effortDays: 2,
    impactHoursPerMonth: 20,
    confidence: "high",
    prerequisites: ["Codebase with type annotations"],
    relatedTech: ["TypeScript", "Python", "Java", "Go", "C#"],
    whyItMatters:
      "Documentation is perpetually outdated. AI can regenerate it on every commit.",
  },
  {
    id: "chatbot-support",
    title: "AI Customer Support Chatbot",
    description:
      "Deploy an AI chatbot trained on your product documentation to handle tier-1 support queries.",
    dimension: "customer-facing",
    effort: "weeks",
    effortDays: 14,
    impactHoursPerMonth: 80,
    confidence: "medium",
    prerequisites: ["Product documentation", "Frontend application"],
    relatedTech: ["React", "Next.js", "Vue", "Angular"],
    whyItMatters:
      "70% of support tickets are repetitive. An AI chatbot can resolve them instantly, 24/7.",
  },
  {
    id: "ci-optimization",
    title: "AI CI/CD Pipeline Optimization",
    description:
      "Use AI to analyze build failures, predict flaky tests, and optimize pipeline run times.",
    dimension: "devops",
    effort: "weeks",
    effortDays: 10,
    impactHoursPerMonth: 25,
    confidence: "medium",
    prerequisites: ["CI/CD pipeline with logs", "Build history data"],
    relatedTech: ["GitHub Actions", "GitLab CI", "Jenkins", "CircleCI"],
    whyItMatters:
      "Slow and flaky CI wastes hours of developer time daily. AI can cut build times 30-50%.",
  },
  {
    id: "data-query-assistant",
    title: "Natural Language Data Querying",
    description:
      "Enable team members to query databases using natural language, powered by text-to-SQL AI models.",
    dimension: "data-pipeline",
    effort: "weeks",
    effortDays: 12,
    impactHoursPerMonth: 35,
    confidence: "medium",
    prerequisites: ["Relational database", "Schema documentation"],
    relatedTech: ["PostgreSQL", "MySQL", "ClickHouse", "Supabase"],
    whyItMatters:
      "Non-technical stakeholders can self-serve data questions instead of waiting for engineering.",
  },
  {
    id: "code-completion",
    title: "AI Code Completion Integration",
    description:
      "Deploy team-wide AI code completion with project-specific context for faster development.",
    dimension: "code-generation",
    effort: "hours",
    effortDays: 0.5,
    impactHoursPerMonth: 60,
    confidence: "high",
    prerequisites: ["IDE (VS Code, JetBrains, etc.)"],
    relatedTech: ["TypeScript", "JavaScript", "Python", "Java", "Go", "Rust"],
    whyItMatters:
      "AI code completion saves 30-50% of typing time and reduces context-switching to docs.",
  },
  {
    id: "incident-detection",
    title: "AI Anomaly Detection for Monitoring",
    description:
      "Add AI-powered anomaly detection to your monitoring stack to catch incidents before users notice.",
    dimension: "devops",
    effort: "weeks",
    effortDays: 15,
    impactHoursPerMonth: 20,
    confidence: "low",
    prerequisites: ["Monitoring/observability platform", "Historical metrics data"],
    relatedTech: ["AWS", "Google Cloud", "Cloudflare"],
    whyItMatters:
      "Proactive detection reduces MTTR by 60% and prevents customer-facing outages.",
  },
  {
    id: "smart-search",
    title: "AI-Powered Semantic Search",
    description:
      "Replace keyword search with vector-based semantic search for your product or internal tools.",
    dimension: "customer-facing",
    effort: "weeks",
    effortDays: 10,
    impactHoursPerMonth: 25,
    confidence: "medium",
    prerequisites: ["Search-enabled application", "Content to index"],
    relatedTech: ["React", "Next.js", "Elasticsearch", "PostgreSQL"],
    whyItMatters:
      "Semantic search understands intent, not just keywords, dramatically improving search relevance.",
  },
  {
    id: "pr-description-gen",
    title: "AI PR Description Generator",
    description:
      "Auto-generate pull request descriptions from diffs, commit messages, and linked issues.",
    dimension: "documentation",
    effort: "hours",
    effortDays: 1,
    impactHoursPerMonth: 15,
    confidence: "high",
    prerequisites: ["Git-based workflow", "CI/CD pipeline"],
    relatedTech: ["GitHub Actions", "GitLab CI"],
    whyItMatters:
      "Well-described PRs speed up reviews and create better audit trails. AI makes it effortless.",
  },
];

/** Score how relevant an opportunity is for the given tech stack */
function relevanceScore(opp: AutomationOpportunity, input: TechStackInput): number {
  let score = 0;

  // Tech match
  const allTech = [
    ...input.languages,
    ...input.frameworks,
    ...input.databases,
    input.infrastructure.cloud,
    input.infrastructure.cicd,
  ];

  const matchCount = opp.relatedTech.filter((t) => allTech.includes(t)).length;
  score += matchCount * 20;

  // Team size -- larger teams get more value from automation
  if (input.team.size >= 10) score += 15;
  else if (input.team.size >= 5) score += 10;
  else if (input.team.size >= 3) score += 5;

  // Confidence bonus
  if (opp.confidence === "high") score += 10;
  else if (opp.confidence === "medium") score += 5;

  // ROI ratio bonus
  const roi = opp.impactHoursPerMonth / Math.max(opp.effortDays, 0.5);
  score += Math.min(roi * 2, 30);

  return score;
}

export function findAutomationOpportunities(
  input: TechStackInput,
  limit = 8,
): AutomationOpportunity[] {
  const scored = OPPORTUNITY_CATALOG.map((opp) => ({
    opp,
    score: relevanceScore(opp, input),
  }));

  scored.sort((a, b) => b.score - a.score);

  return scored.slice(0, limit).map((s) => s.opp);
}
